import {
  isRouteErrorResponse,
  Links,
  type LinksFunction,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useRouteError,
} from "react-router";

import appStylesHref from "./styles/app.css?url";

/**
 * Document <link>s, rendered by <Links />.
 *
 * - Source Sans Pro is BigCommerce's admin typeface; loading it here is the
 *   single biggest "reads native" win. preconnect first so the font request
 *   starts as early as possible.
 * - app.css is the static base theme (palette tokens, typography, form/table
 *   defaults). Shipping styling as a plain stylesheet — instead of
 *   styled-components/BigDesign — is what keeps SSR clean and hydration safe.
 */
export const links: LinksFunction = () => [
  { rel: "preconnect", href: "https://fonts.googleapis.com" },
  {
    rel: "preconnect",
    href: "https://fonts.gstatic.com",
    crossOrigin: "anonymous",
  },
  {
    rel: "stylesheet",
    href: "https://fonts.googleapis.com/css2?family=Source+Sans+Pro:wght@400;600;700&display=swap",
  },
  { rel: "stylesheet", href: appStylesHref },
];

export default function App() {
  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <title>MarketBasketAnalysis for BigCommerce</title>
        <Meta />
        <Links />
      </head>
      <body>
        <Outlet />
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

/**
 * Document-level ErrorBoundary: the final backstop. The /app route has its own
 * branded ErrorBoundary (see routes/app.tsx) that catches loader/action throws
 * across the embedded admin, but a throw ABOVE that route (in this root, or on a
 * non-/app route) would otherwise render nothing. This renders a self-contained
 * HTML document with a friendly, present-tense message so the merchant always
 * sees something recoverable rather than a blank "Application Error" screen.
 *
 * It renders its own <html>/<body> because a root-level boundary replaces the
 * whole document tree. Copy follows the repo user-facing rules (no em dashes,
 * present tense).
 */
export function ErrorBoundary() {
  const error = useRouteError();
  const isRateLimit =
    isRouteErrorResponse(error) &&
    (error.status === 429 ||
      (typeof error.data === "string" && /rate.?limit/i.test(error.data)));
  const message = isRateLimit
    ? "This page hit a BigCommerce rate limit. That is usually temporary and it clears on its own. Wait a moment, then reload."
    : "Something went wrong loading this page. This is usually a brief hiccup. Wait a moment, then reload.";
  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <title>MarketBasketAnalysis for BigCommerce</title>
        <Meta />
        <Links />
      </head>
      <body>
        <main
          role="alert"
          style={{
            fontFamily:
              "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
            maxWidth: 520,
            margin: "0 auto",
            padding: "3rem 1.5rem",
            textAlign: "center",
            color: "#313440",
          }}
        >
          <h1
            style={{
              margin: "0 0 0.75rem",
              fontSize: "1.35rem",
              fontWeight: 700,
              color: "#0d52ff",
            }}
          >
            Something went wrong
          </h1>
          <p style={{ margin: "0 0 1.5rem", fontSize: "1rem", lineHeight: 1.5 }}>
            {message}
          </p>
          <button
            type="button"
            onClick={() => {
              if (typeof window !== "undefined") window.location.reload();
            }}
            style={{
              appearance: "none",
              cursor: "pointer",
              border: "none",
              borderRadius: 8,
              padding: "0.6rem 1.2rem",
              fontFamily: "inherit",
              fontSize: "0.95rem",
              fontWeight: 600,
              color: "#ffffff",
              background: "#0d52ff",
            }}
          >
            Reload
          </button>
        </main>
        <Scripts />
      </body>
    </html>
  );
}
